import { t as tr } from './i18n';
import type { PublicPlan } from './rfqFiles';
import type { PublicSupplierProfile } from './supplierProfile';

/**
 * Oferta trimisă de furnizor prin link (Echipă, Companie). Mailul trimis din
 * Procumi are un buton către o pagină fără cont, pe care furnizorul trece
 * prețurile și condițiile; oferta intră direct în tabelul comparativ, cu
 * `channel = 'link'`. Linkul e al unui singur furnizor, pe o singură cerere.
 */

/** Canalul ofertelor venite prin link (`Offer.channel`). */
export const LINK_CHANNEL = 'link';

export const OFFER_LINK_LABEL = 'Completează oferta online';

/** `?oferta=<token>`: pagina publică a furnizorului. */
export const offerLink = (base: string, token: string): string =>
  `${base.replace(/\/+$/, '')}/?oferta=${token}`;

export interface PublicOfferItem {
  id: string;
  desc: string;
  qty: number;
  unit: string;
  /** închiriere: zilele cerute; null = cumpărare */
  rentDays: number | null;
}

/** Ce trimite furnizorul. Prețurile sunt unitare, în lei, fără TVA. */
export interface PublicOfferInput {
  /** itemId → preț unitar; pozițiile fără preț lipsesc */
  prices: Record<string, number>;
  /** itemId → produsul propus în loc, doar unde e altul */
  alts: Record<string, string>;
  deliveryDays: number | null;
  /** `deliveryDays` sunt zile lucrătoare */
  deliveryWorking?: boolean;
  paymentDays: number | null;
  /** 'AAAA-LL-ZZ' */
  validUntil: string | null;
  /** '' = nu spune, 'included' = inclus în prețuri, 'extra' = separat */
  transport: '' | 'included' | 'extra';
  transportCost: number | null;
  notes: string;
}

/**
 * - open: se poate trimite sau schimba oferta
 * - closed: cererea s-a încheiat (furnizor ales, închisă, anulată)
 * - taken: oferta a fost trecută deja de cumpărător, din alt canal
 * - off: firma nu mai are trimiterea prin link în pachet
 */
export type PublicOfferState = 'open' | 'closed' | 'taken' | 'off';

export interface PublicOffer {
  state: PublicOfferState;
  company: string;
  /** cuvântul firmei pentru șantier (siteTerms.ts): furnizorul vede „Necesar la punctul de lucru” */
  siteTerm: string;
  /** limba furnizorului: pagina se deschide în ea */
  locale: 'ro' | 'en';
  /** moneda și țara firmei care cumpără: prețurile se cer în moneda ei */
  currency: string;
  country: string;
  buyer: { name: string; email: string };
  supplier: string;
  title: string;
  /** „Bloc Turnului, Brașov” */
  deliveryTo: string;
  /** 'AAAA-LL-ZZ' */
  deadline: string;
  neededBy: string | null;
  note: string;
  items: PublicOfferItem[];
  /** planurile cererii, de descărcat; [] când cererea s-a încheiat */
  files: PublicPlan[];
  /** datele firmei furnizorului, pe care și le poate trece singur */
  profile: PublicSupplierProfile;
  /** ISO 8601: când a trimis furnizorul oferta prin link; null = încă nu */
  submittedAt: string | null;
  /** oferta trimisă prin link, ca s-o poată schimba */
  offer: PublicOfferInput | null;
  /** furnizorul își poate încărca oferta ca fișier (PDF, Excel, poză); Atlas o citește (Ticket 08) */
  upload: boolean;
  /** documentele încărcate de furnizor pe link, cele mai noi primele */
  documents: { filename: string; size: number; uploadedAt: string }[];
}

/** Ce primește încărcarea de pe link: PDF, Excel (.xlsx, .xls) și poze. */
export const PUBLIC_UPLOAD_ACCEPT = '.pdf,.xlsx,.xls,.png,.jpg,.jpeg,.webp';
export const PUBLIC_UPLOAD_MAX_BYTES = 15 * 1024 * 1024;

/** Totalul fără TVA al pozițiilor cu preț (fără transport). */
export function publicOfferTotal(
  items: Pick<PublicOfferItem, 'id' | 'qty'>[],
  prices: Record<string, number | null | undefined>,
): number {
  return items.reduce((sum, it) => {
    const p = prices[it.id];
    return p == null ? sum : sum + p * it.qty;
  }, 0);
}

export const PUBLIC_OFFER_STATE_TEXT: Record<Exclude<PublicOfferState, 'open'>, string> = {
  get closed() {
    return tr('Nu mai primește oferte. Mulțumim pentru timpul acordat!');
  },
  get taken() {
    return tr(
      'Oferta voastră a fost deja primită și trecută în tabel. Pentru schimbări, răspundeți la mail.',
    );
  },
  get off() {
    return tr('Linkul nu mai e activ. Trimiteți oferta răspunzând la mail.');
  },
};
